import {
  Calendar,
  Select,
  Input,
  Checkbox,
  Button,
  message,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import { get, post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useLocation } from "react-router-dom";
dayjs.extend(isSameOrBefore);

const LogHours = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [comments, setComments] = useState("");
  const [sameCompanyHours, setSameCompanyHours] = useState(false);

  const [view_restHours_data, setView_restHours_data] = useState(null);

  const [selectedSlots, setSelectedSlots] = useState([]);
  const [disabledSlots, setDisabledSlots] = useState([]);

  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState(null);
  const [calendarDisplayDate, setCalendarDisplayDate] = useState(dayjs());
  const lastFetchedMonthKey = useRef(null);

  const today = dayjs();

  const location = useLocation();

  const signOnDate = view_restHours_data?.seafarer_sign_on_date
    ? dayjs(view_restHours_data?.seafarer_sign_on_date)
    : null;

  const signOffDate = view_restHours_data?.seafarer_sign_off_date
    ? dayjs(view_restHours_data?.seafarer_sign_off_date)
    : null;

  const selectedDay = useMemo(() => {
    if (!view_restHours_data?.days) return null;
    return view_restHours_data.days.find(
      (d) => d.date === selectedDate.format("YYYY-MM-DD"),
    );
  }, [view_restHours_data, selectedDate]);

  const showPlannedHours = !selectedDay?.has_data;

  const hasExistingWorkingHours = useMemo(() => {
    return !!(
      selectedDay?.working_hours && selectedDay.working_hours.length > 0
    );
  }, [selectedDay]);

  const isSelectedDatePast = useMemo(() => {
    return selectedDate.isBefore(today, "day");
  }, [selectedDate, today]);

  const rest_hours_data = async (date = selectedDate, force = false) => {
    const monthKey = `${date.year()}-${String(date.month() + 1).padStart(2, "0")}`;

    if (!force && lastFetchedMonthKey.current === monthKey) return;

    try {
      const formData = new FormData();
      formData.append("year", date.year());
      formData.append("month", date.month() + 1);
      if (location.state?.staff_id)
        formData.append("staff_id", location.state?.staff_id);

      const res = await post(ApiURL.view_rest_hours_record_st, formData);

      if (res?.status) {
        setView_restHours_data(res.data);
        lastFetchedMonthKey.current = monthKey;
      }
    } catch (err) {
      console.log("API Error =>", err);
    }
  };

  // ─── Shared helper: is a given slot index "green by default"? ───────────────
  // This single function is the source of truth used by both the render loop
  // and getWorkingHoursPayload so they can never drift apart.
  const isSlotDefaultGreen = useCallback(
    (
      index,
      { plannedRanges, workingRanges, hasData, isWorkingDataAvailable },
    ) => {
      const currentMinutes =
        Math.floor(index / 2) * 60 + (index % 2 === 0 ? 0 : 30);

      const isPlanned = plannedRanges.some((range) => {
        const [startH, startM] = range.start.split(":").map(Number);
        const [endH, endM] = range.end.split(":").map(Number);
        return (
          currentMinutes >= startH * 60 + startM &&
          currentMinutes < endH * 60 + endM
        );
      });

      const isWorking = workingRanges.some((range) => {
        const [startH, startM] = range.start_time.split(":").map(Number);
        const [endH, endM] = range.end_time.split(":").map(Number);
        return (
          currentMinutes >= startH * 60 + startM &&
          currentMinutes < endH * 60 + endM
        );
      });

      // When data already exists for the day, always show working hours as
      // the default-green baseline (regardless of sameCompanyHours).
      // When no data yet, respect the sameCompanyHours toggle.
      if (hasData) {
        return isWorking;
      }
      return sameCompanyHours ? isPlanned : false;
    },
    [sameCompanyHours],
  );

  // ─── Derive final slot state (green or not) from defaults + user overrides ──
  const getSlotFinalGreen = useCallback(
    (index, defaultGreen) => {
      return defaultGreen
        ? !disabledSlots.includes(index)
        : selectedSlots.includes(index);
    },
    [disabledSlots, selectedSlots],
  );

  const save_log = async () => {
    try {
      const formData = new FormData();
      formData.append("date", selectedDate.format("YYYY-MM-DD"));
      formData.append(
        "working_hours",
        JSON.stringify(getWorkingHoursPayload()),
      );
      formData.append("entry_type", "work");
      formData.append("description", comments);
      if (location.state?.staff_id)
        formData.append("staff_id", location.state?.staff_id);

      const res = await post(ApiURL.add_rest_hours_st, formData);

      if (res?.status) {
        message.success("Data saved successfully");
        // Force-refresh so the calendar and violations reflect the new record.
        await rest_hours_data(selectedDate, true);
        // Reset overrides – the refreshed working_hours are now the new baseline.
        resetSlotOverrides();
      }
    } catch (error) {
      console.log(error);
      message.error("Something went wrong");
    }
  };

  const edit_log = async () => {
    try {
      const formData = new FormData();

      formData.append("log_id", selectedDay.entries[0].id);

      formData.append(
        "working_hours",
        JSON.stringify(getWorkingHoursPayload()),
      );

      formData.append("description", comments);
      if (location.state?.staff_id)
        formData.append("staff_id", location.state?.staff_id);

      const res = await post(ApiURL.edit_log_rest_hours_st, formData);

      if (res?.status) {
        message.success("Hours updated successfully");
        // Force-refresh so violations and working hours reflect the edit.
        await rest_hours_data(selectedDate, true);
        // Reset overrides – the refreshed working_hours are now the new baseline.
        resetSlotOverrides();
      }
    } catch (err) {
      console.log(err);
      message.error("Failed to update hours");
    }
  };

  // ─── Clear user drag-overrides (call after any successful save/edit) ─────────
  const resetSlotOverrides = () => {
    setSelectedSlots([]);
    setDisabledSlots([]);
    setSameCompanyHours(false);
  };

  const plannedRanges = useMemo(() => {
    return view_restHours_data?.planned_hours || [];
  }, [view_restHours_data]);

  const workingRanges = useMemo(() => {
    const hours = selectedDay?.working_hours || [];
    return hours.map((item) => {
      if (typeof item === "string") {
        const [start, end] = item.split("-");
        return { start_time: start, end_time: end };
      }
      return {
        start_time: item.start_time ?? item.start,
        end_time: item.end_time ?? item.end,
      };
    });
  }, [selectedDay]);

  const isWorkingDataAvailable = workingRanges.length > 0;

  // Shared context object passed to isSlotDefaultGreen
  const slotContext = useMemo(
    () => ({
      plannedRanges,
      workingRanges,
      hasData: !!selectedDay?.has_data,
      isWorkingDataAvailable,
    }),
    [plannedRanges, workingRanges, selectedDay, isWorkingDataAvailable],
  );

  const getWorkingHoursPayload = () => {
    const activeSlots = [];

    for (let index = 0; index < 48; index++) {
      const defaultGreen = isSlotDefaultGreen(index, slotContext);
      const finalGreen = getSlotFinalGreen(index, defaultGreen);
      if (finalGreen) activeSlots.push(index);
    }

    if (!activeSlots.length) return [];

    const ranges = [];
    let start = activeSlots[0];
    let prev = activeSlots[0];

    for (let i = 1; i < activeSlots.length; i++) {
      const current = activeSlots[i];
      if (current === prev + 1) {
        prev = current;
      } else {
        ranges.push({ start, end: prev + 1 });
        start = current;
        prev = current;
      }
    }
    ranges.push({ start, end: prev + 1 });

    const formatTime = (slotIndex) => {
      if (slotIndex >= 48) return "23:59";
      const totalMinutes = slotIndex * 30;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    };

    return ranges.map((range) => ({
      start: formatTime(range.start),
      end: formatTime(range.end),
    }));
  };

  const handleSlotClick = (index, isGreenByDefault) => {
    if (isGreenByDefault) {
      setDisabledSlots((prev) =>
        prev.includes(index)
          ? prev.filter((i) => i !== index)
          : [...prev, index],
      );
    } else {
      setSelectedSlots((prev) =>
        prev.includes(index)
          ? prev.filter((i) => i !== index)
          : [...prev, index],
      );
    }
  };

  const disabledDate = (current) => {
    if (!current) return false;

    const isFuture = current.isAfter(dayjs(), "day");

    const isOutsideCurrentMonth =
      current.month() !== calendarDisplayDate.month() ||
      current.year() !== calendarDisplayDate.year();

    const beforeSignOn = signOnDate && current.isBefore(signOnDate, "day");

    const afterSignOff = signOffDate && current.isAfter(signOffDate, "day");

    return isFuture || isOutsideCurrentMonth || beforeSignOn || afterSignOff;
  };

  const handleDragSlot = (index, defaultGreen, finalGreen) => {
    if (!isDragging) return;

    if (dragMode === "add") {
      if (defaultGreen) {
        setDisabledSlots((prev) => prev.filter((i) => i !== index));
      } else {
        setSelectedSlots((prev) =>
          prev.includes(index) ? prev : [...prev, index],
        );
      }
    }

    if (dragMode === "remove") {
      if (defaultGreen) {
        setDisabledSlots((prev) =>
          prev.includes(index) ? prev : [...prev, index],
        );
      } else {
        setSelectedSlots((prev) => prev.filter((i) => i !== index));
      }
    }
  };

  const calculatedWorkHours = useMemo(() => {
    let greenCount = 0;

    for (let index = 0; index < 48; index++) {
      const defaultGreen = isSlotDefaultGreen(index, slotContext);
      const finalGreen = getSlotFinalGreen(index, defaultGreen);
      if (finalGreen) greenCount++;
    }

    return greenCount * 0.5;
  }, [isSlotDefaultGreen, getSlotFinalGreen, slotContext]);

  const calculatedRestHours = useMemo(
    () => 24 - calculatedWorkHours,
    [calculatedWorkHours],
  );

  const getDateCellStyle = (date) => {
    const dateStr = date.format("YYYY-MM-DD");
    const isFuture = date.isAfter(today, "day");
    const isToday = date.isSame(today, "day");
    const isSelected = dateStr === selectedDate.format("YYYY-MM-DD");
    const beforeSignOn = signOnDate && date.isBefore(signOnDate, "day");

    const afterSignOff = signOffDate && date.isAfter(signOffDate, "day");

    const dayData = view_restHours_data?.days?.find((d) => d.date === dateStr);
    const isEdited =
      dayData?.entries?.some((entry) => entry.edited === true) || false;

    if (isFuture || beforeSignOn || afterSignOff) {
      return {
        bg: "#f3f4f6",
        border: "1px solid #d1d5db",
        color: "#9ca3af",
        cursor: "not-allowed",
        tooltip: null,
      };
    }

    if (dayData) {
      const hasWorkHours =
        dayData.working_hours && dayData.working_hours.length > 0;
      const isCompliant = dayData.compliant;
      const violations = dayData.violations || [];
      const violationText = violations
        .map((v) => `${v.rule?.replaceAll("_", " ")}: ${v.detail}`)
        .join("\n");

      if (!isCompliant) {
        return {
          bg: isSelected ? "#fee2e2" : "#fecaca",
          border: isSelected ? "2px solid #dc2626" : "1px solid #f87171",
          color: "#991b1b",
          cursor: "pointer",
          tooltip: violationText || "Compliance violation",
        };
      }

      if (isCompliant && hasWorkHours) {
        if (isToday) {
          return {
            bg: isSelected ? "#bbf7d0" : "#22c55e",
            border: isSelected ? "2px solid #15803d" : "1px solid #16a34a",
            color: isSelected ? "#14532d" : "#fff",
            cursor: "pointer",
            tooltip: null,
          };
        }

        if (isEdited) {
          return {
            bg: isSelected ? "#bfdbfe" : "#3b82f6",
            border: isSelected ? "2px solid #1d4ed8" : "1px solid #2563eb",
            color: isSelected ? "#1e3a8a" : "#fff",
            cursor: "pointer",
            tooltip: "Edited",
          };
        }

        return {
          bg: isSelected ? "#bbf7d0" : "#22c55e",
          border: isSelected ? "2px solid #15803d" : "1px solid #16a34a",
          color: isSelected ? "#14532d" : "#fff",
          cursor: "pointer",
          tooltip: null,
        };
      }
    }

    if (isEdited) {
      return {
        bg: isSelected ? "#bfdbfe" : "#3b82f6",
        border: isSelected ? "2px solid #1d4ed8" : "1px solid #2563eb",
        color: isSelected ? "#1e3a8a" : "#fff",
        cursor: "pointer",
        tooltip: "Edited",
      };
    }

    return {
      bg: "transparent",
      border: isSelected ? "2px solid #111827" : "1px solid transparent",
      color: "#111827",
      cursor: "pointer",
      tooltip: null,
    };
  };

  const selectedDayViolations = useMemo(() => {
    if (!selectedDay?.violations) return [];
    return selectedDay.violations;
  }, [selectedDay]);

  // Reset overrides whenever the selected date changes
  useEffect(() => {
    setSelectedSlots([]);
    setDisabledSlots([]);
    setSameCompanyHours(false);
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDay?.entries?.length) {
      setComments(
        selectedDay.entries
          .map((e) => e.description)
          .filter(Boolean)
          .join(", "),
      );
    } else {
      setComments("");
    }
  }, [selectedDay]);

  const currentMonthKey = `${selectedDate.year()}-${String(selectedDate.month() + 1).padStart(2, "0")}`;
  useEffect(() => {
    rest_hours_data(selectedDate);
  }, [currentMonthKey]);

  console.log("selectedDate", selectedDate);
  console.log("selectedDay", selectedDay);

  return (
    <div style={{ padding: "14px", background: "#f3f4f6", minHeight: "100vh" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          gap: "14px",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: "14px",
            padding: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
            height: "fit-content",
          }}
        >
          <Calendar
            fullscreen={false}
            value={calendarDisplayDate}
            disabledDate={disabledDate}
            onSelect={(date) => {
              const beforeSignOn =
                signOnDate && date.isBefore(signOnDate, "day");

              const afterSignOff =
                signOffDate && date.isAfter(signOffDate, "day");

              if (date.isAfter(today, "day") || beforeSignOn || afterSignOff) {
                return;
              }

              setSelectedDate(date);
              setCalendarDisplayDate(date);
            }}
            onPanelChange={(date) => {
              setCalendarDisplayDate(date);
            }}
            fullCellRender={(date) => {
              const isCurrentMonth =
                date.month() === calendarDisplayDate.month();

              if (!isCurrentMonth) {
                return (
                  <div
                    style={{
                      height: "32px",
                      width: "32px",
                      visibility: "hidden",
                      opacity: 0,
                      pointerEvents: "none",
                    }}
                  />
                );
              }

              const cellStyle = getDateCellStyle(date);

              const cell = (
                <div
                  style={{
                    height: "32px",
                    width: "32px",
                    margin: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "600",
                    borderRadius: "6px",
                    color: cellStyle.color,
                    background: cellStyle.bg,
                    border: cellStyle.border,
                    cursor: cellStyle.cursor,
                    transition: "all 0.15s ease",
                  }}
                >
                  {date.date()}
                </div>
              );

              return cellStyle.tooltip ? (
                <Tooltip title={cellStyle.tooltip}>{cell}</Tooltip>
              ) : (
                cell
              );
            }}
            headerRender={({ value, onChange }) => {
              const current = value.clone();

              const months = [];
              for (let i = 0; i < 12; i++) {
                months.push(
                  <Select.Option key={i} value={i}>
                    {dayjs().month(i).format("MMM")}
                  </Select.Option>,
                );
              }

              const years = [];
              for (let i = 1980; i <= 2200; i++) {
                years.push(
                  <Select.Option key={i} value={i}>
                    {i}
                  </Select.Option>,
                );
              }

              return (
                <div
                  style={{ display: "flex", gap: "6px", marginBottom: "8px" }}
                >
                  <Select
                    size="small"
                    value={current.month()}
                    style={{ width: "50%" }}
                    onChange={(newMonth) => {
                      const updated = current.clone().month(newMonth);
                      onChange(updated);
                      setCalendarDisplayDate(updated);
                      rest_hours_data(updated);
                    }}
                  >
                    {months}
                  </Select>

                  <Select
                    size="small"
                    value={current.year()}
                    style={{ width: "50%" }}
                    onChange={(newYear) => {
                      const updated = current.clone().year(newYear);
                      onChange(updated);
                      setCalendarDisplayDate(updated);
                      rest_hours_data(updated);
                    }}
                  >
                    {years}
                  </Select>
                </div>
              );
            }}
          />
          <div
            style={{
              marginTop: "8px",
              padding: "8px 4px",
              borderTop: "1px solid #f1f5f9",
            }}
          >
            {[
              { color: "#22c55e", label: "Compliant" },
              { color: "#ef4444", label: "Non-compliant" },
              { color: "#3b82f6", label: "Edited" },
              { color: "#9ca3af", label: "Future / No data" },
            ].map(({ color, label }) => (
              <div
                key={label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "4px",
                }}
              >
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "3px",
                    background: color,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: "11px", color: "#6b7280" }}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div
          style={{
            background: "#fff",
            borderRadius: "20px",
            padding: "20px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
            border: "1px solid #eef2f7",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              paddingBottom: "12px",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#111827",
                }}
              >
                Current Status
              </h2>
              <p
                style={{
                  margin: "4px 0 0",
                  color: "#6b7280",
                  fontSize: "13px",
                }}
              >
                Track and manage your working schedule
              </p>
            </div>
            {isSelectedDatePast && hasExistingWorkingHours && (
              <div
                style={{
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "8px",
                  padding: "4px 10px",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#1d4ed8",
                }}
              >
                Editing Past Date: {selectedDate.format("DD MMM YYYY")}
              </div>
            )}
          </div>

          {selectedDayViolations.length > 0 && (
            <div
              style={{
                background: "#fff7ed",
                border: "1px solid #fdba74",
                borderRadius: "12px",
                padding: "14px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "12px",
                }}
              >
                <span style={{ fontSize: "16px" }}>⚠️</span>

                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: "#c2410c",
                  }}
                >
                  Compliance Violations ({selectedDayViolations.length})
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                {selectedDayViolations.map((violation, index) => (
                  <div
                    key={index}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      background: "#fff",
                      border: "1px solid #fdba74",
                      borderRadius: "999px",
                      padding: "6px 12px",
                      fontSize: "12px",
                      color: "#9a3412",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "700",
                      }}
                    >
                      {violation.rule?.replaceAll("_", " ")}
                    </span>

                    <span
                      style={{
                        color: "#7c2d12",
                      }}
                    >
                      {violation.detail}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "13px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Comments
            </label>

            <Input.TextArea
              rows={4}
              placeholder="Add comments here..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
            }}
          >
            <div
              style={{
                background:
                  "linear-gradient(135deg, #22c55e15 0%, #22c55e25 100%)",
                border: "1px solid #bbf7d0",
                borderRadius: "16px",
                padding: "18px",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#166534",
                }}
              >
                Working Hours
              </div>
              <div
                style={{
                  fontSize: "30px",
                  fontWeight: "700",
                  color: "#15803d",
                }}
              >
                {calculatedWorkHours}
              </div>
              <div
                style={{ marginTop: "6px", fontSize: "12px", color: "#166534" }}
              >
                Hours Logged
              </div>
            </div>

            <div
              style={{
                background:
                  "linear-gradient(135deg, #a855f715 0%, #a855f725 100%)",
                border: "1px solid #d8b4fe",
                borderRadius: "16px",
                padding: "18px",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#7e22ce",
                }}
              >
                Rest Hours
              </div>
              <div
                style={{
                  fontSize: "30px",
                  fontWeight: "700",
                  color: "#9333ea",
                }}
              >
                {calculatedRestHours}
              </div>
              <div
                style={{ marginTop: "6px", fontSize: "12px", color: "#7e22ce" }}
              >
                Remaining Hours
              </div>
            </div>
          </div>
        </div>
      </div>

      {!hasExistingWorkingHours && !selectedDay?.has_data && (
        <div style={{ marginTop: "10px" }}>
          <Checkbox
            checked={sameCompanyHours}
            onChange={(e) => setSameCompanyHours(e.target.checked)}
          >
            Followed Company Schedule
          </Checkbox>
        </div>
      )}

      <br />

      <div
        style={{
          background: "#fff",
          borderRadius: "14px",
          padding: "16px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontWeight: "700",
              fontSize: "16px",
              color: "#111827",
            }}
          >
            Working Hours Timeline{" "}
            <span
              style={{ color: "#6b7280", fontSize: "12px", fontWeight: "400" }}
            >
              (
              {hasExistingWorkingHours
                ? "Existing hours shown in green. Edit by dragging on the timeline."
                : "Drag to select your working hours. Planned hours shown in gray."}
              )
            </span>
          </h2>

          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <div
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "3px",
                  background: "#22c55e",
                }}
              />
              <span style={{ fontSize: "11px", color: "#6b7280" }}>
                Working hours
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {!selectedDay?.has_data && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <div
                    style={{
                      width: "14px",
                      height: "14px",
                      borderRadius: "3px",
                      background: "#9ca3af",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#6b7280",
                    }}
                  >
                    Planned hours
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(48, 1fr)",
            marginBottom: "4px",
          }}
        >
          {Array.from({ length: 24 }, (_, hour) => (
            <div
              key={hour}
              style={{
                gridColumn: "span 2",
                textAlign: "center",
                fontSize: "9px",
                fontWeight: "700",
              }}
            >
              {hour}
            </div>
          ))}
        </div>

        <div
          onMouseUp={() => {
            setIsDragging(false);
            setDragMode(null);
          }}
          onMouseLeave={() => {
            setIsDragging(false);
            setDragMode(null);
          }}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(48, 1fr)",
            borderTop: "2px solid #374151",
            borderLeft: "2px solid #374151",
            borderBottom: "2px solid #374151",
            overflow: "hidden",
          }}
        >
          {Array.from({ length: 48 }, (_, index) => {
            const hour = Math.floor(index / 2);
            const startHour = hour.toString().padStart(2, "0");
            const nextHour = (hour + 1).toString().padStart(2, "0");
            const label =
              index % 2 === 0
                ? `${startHour}:00–${startHour}:30`
                : `${startHour}:30–${nextHour}:00`;

            const defaultGreen = isSlotDefaultGreen(index, slotContext);
            const finalGreen = getSlotFinalGreen(index, defaultGreen);

            const currentMinutes =
              Math.floor(index / 2) * 60 + (index % 2 === 0 ? 0 : 30);
            const isPlanned = plannedRanges.some((range) => {
              const [startH, startM] = range.start.split(":").map(Number);
              const [endH, endM] = range.end.split(":").map(Number);
              return (
                currentMinutes >= startH * 60 + startM &&
                currentMinutes < endH * 60 + endM
              );
            });

            return (
              <Tooltip key={index} title={label} mouseEnterDelay={0.5}>
                <div
                  onMouseDown={() => {
                    setIsDragging(true);
                    setDragMode(finalGreen ? "remove" : "add");
                    handleSlotClick(index, defaultGreen);
                  }}
                  onMouseEnter={() =>
                    handleDragSlot(index, defaultGreen, finalGreen)
                  }
                  style={{
                    height: "34px",
                    background: finalGreen
                      ? "#22c55e"
                      : showPlannedHours && isPlanned
                        ? "#9ca3af"
                        : "#fff",
                    cursor: "pointer",
                    borderRight:
                      index % 2 === 0
                        ? "1px solid #d1d5db"
                        : "2px solid #374151",
                  }}
                />
              </Tooltip>
            );
          })}
        </div>
      </div>

      {/* SAVE / EDIT BUTTON */}
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "14px" }}
      >
        {hasExistingWorkingHours ? (
          <Button
            type="primary"
            onClick={edit_log}
            style={{
              height: "34px",
              paddingInline: "20px",
              borderRadius: "6px",
              fontWeight: "600",
              fontSize: "13px",
              background: "#2563eb",
              borderColor: "#1d4ed8",
            }}
          >
            Edit Hours
          </Button>
        ) : (
          <Button
            type="primary"
            onClick={save_log}
            style={{
              height: "34px",
              paddingInline: "20px",
              borderRadius: "6px",
              fontWeight: "600",
              fontSize: "13px",
            }}
          >
            Save Hours
          </Button>
        )}
      </div>
    </div>
  );
};

export default LogHours;
